import { Popover } from 'antd'
import { Input, Checkbox } from '@/components/ui'
import { SearchOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { ChevronDownIcon } from '@/components/icons/EventsIcons'

interface FilterItem {
  id?: string | number
  name: string
}

interface FilterPopoverProps<T extends FilterItem> {
  label: string
  items: T[]
  selectedItems: string[]
  onSelectionChange: (ids: string[]) => void
  visible: boolean
  onVisibleChange: (visible: boolean) => void
  renderItem?: (item: T) => ReactNode
}

export function FilterPopover<T extends FilterItem>({
  label,
  items,
  selectedItems,
  onSelectionChange,
  visible,
  onVisibleChange,
  renderItem,
}: FilterPopoverProps<T>) {
  const handleCheckboxChange = (itemId: string | number | undefined, checked: boolean) => {
    if (itemId === undefined || itemId === null) return
    const normalizedId = itemId.toString()
    if (checked) {
      onSelectionChange([...selectedItems, normalizedId])
    } else {
      onSelectionChange(selectedItems.filter((id) => id !== normalizedId))
    }
  }

  const content = (
    <div className="w-[240px] p-1">
      <Input
        placeholder={`Search ${label.toLowerCase()}`}
        prefix={<SearchOutlined className="text-gray-400" />}
        className="mb-2"
      />
      <div className="max-h-[200px] overflow-y-auto">
        {items.map((item) => {
          const itemId = item.id?.toString()
          return (
            <div key={itemId || item.name} className="flex items-center p-1 hover:bg-gray-50 rounded">
              <Checkbox
                checked={itemId ? selectedItems.includes(itemId) : false}
                onChange={(e) => handleCheckboxChange(itemId, e.target.checked)}
                className="flex-grow"
              >
                {renderItem ? renderItem(item) : <span>{item.name}</span>}
              </Checkbox>
            </div>
          )
        })}
      </div>
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      visible={visible}
      onVisibleChange={onVisibleChange}
      placement="bottom"
      overlayClassName="filter-popover"
    >
      <div
        className={classNames(
          'flex items-center justify-between border border-[#d9d9d9] bg-white px-4 h-[32px] text-sm font-normal min-w-[120px] cursor-pointer',
          {
            'text-blue-500 border-blue-500': selectedItems.length > 0,
          }
        )}
        onClick={() => onVisibleChange(!visible)}
        style={{ lineHeight: '32px' }}
      >
        <div className="flex items-center">
          <span className="font-medium">{label}</span>
          {selectedItems.length > 0 && (
            <span className="ml-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
              {selectedItems.length}
            </span>
          )}
        </div>
        {selectedItems.length === 0 && <ChevronDownIcon />}
      </div>
    </Popover>
  )
}
